import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ScoreCard from './ScoreCard';
import {
  canonicalTeamSlug,
  isDivisionOneConference,
  normalizeConference,
} from '../data/teamIdentity';
import {
  CONFERENCE_FAVORITES_EVENT,
  CONFERENCE_FAVORITES_STORAGE_KEY,
  getTeamConferenceKey,
  readConferenceFavorites,
} from '../services/favorites';

const FAVORITES_STORAGE_KEY =
  'volleywire.favoriteTeams.v1';

const INITIAL_VISIBLE_GAMES = 30;
const LOAD_MORE_GAMES = 30;

const STATUS_GROUPS = [
  { value: 'other', label: 'Paused / changed', countLabel: 'Paused / changed' },
  {
    value: 'live',
    label: 'Live now',
    countLabel: 'Live',
  },
  {
    value: 'scheduled',
    label: 'Upcoming',
    countLabel: 'Upcoming',
  },
  {
    value: 'final',
    label: 'Final',
    countLabel: 'Final',
  },
];

function LoadingScoreboard() {
  return (
    <div
      className="state-grid"
      aria-label="Loading scores"
      aria-live="polite"
    >
      {Array.from(
        {
          length: 6,
        },
        (_, index) => (
          <div
            className="score-card skeleton"
            key={index}
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
            <span />
          </div>
        )
      )}
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}) {
  return (
    <div
      className="empty-state"
      role="alert"
    >
      <span
        className="state-icon"
        aria-hidden="true"
      >
        !
      </span>

      <h2>
        We couldn’t load the
        scoreboard.
      </h2>

      <p>
        {error ||
          'Something went wrong while loading the latest matches.'}
      </p>

      <button
        type="button"
        onClick={onRetry}
      >
        Try again
      </button>
    </div>
  );
}

function EmptyState({ onShowUpcoming }) {
  return (
    <div className="empty-state">
      <span
        className="state-icon"
        aria-hidden="true"
      >
        VW
      </span>

      <h2>
        No matches are listed for
        this date.
      </h2>

      <p>No published matches on this date. View the upcoming schedule to find the next games.</p>

      {onShowUpcoming ? (
        <button type="button" onClick={onShowUpcoming}>
          View upcoming matches
        </button>
      ) : null}
    </div>
  );
}

function FilteredEmptyState({
  status,
  favoriteCount,
  activePersonalFilters = [],
  personalFilterCounts = {},
  selectedConference,
  query,
  onClear,
}) {
  let title =
    'No matches fit these filters.';

  let description =
    'Try another team name, conference, or match status.';

  if (
    activePersonalFilters.includes('teams') &&
    !personalFilterCounts.teams
  ) {
    title = 'No saved teams yet.';
    description = 'Select a team star on Scores, Teams, Rankings, or Leaders to build My Teams.';
  } else if (
    activePersonalFilters.includes('conferences') &&
    !personalFilterCounts.conferences
  ) {
    title = 'No saved conferences yet.';
    description = 'Open Conferences and select a conference star to build My Conferences.';
  } else if (
    activePersonalFilters.length
  ) {
    title = 'No priority matches on this date.';
    description = 'Try another date or clear My Teams, Top 25, or My Conferences.';
  }

  if (!activePersonalFilters.length) {
    if (status === 'favorites' && favoriteCount === 0) {
      title = 'No favorite teams yet.';
      description = 'Select the star beside a team to save it to My Teams.';
    } else if (status === 'favorites') {
      title = 'Your teams are not playing on this date.';
      description = 'Choose another date or clear the My Teams filter.';
    } else if (selectedConference !== 'all') {
      title = 'No matches from this conference.';
      description = 'Choose another conference or clear the current filters.';
    } else if (query.trim()) {
      title = 'No matching teams found.';
      description = 'Check the team name or clear the search field.';
    }
  }

  return (
    <div className="empty-state">
      <span
        className="state-icon"
        aria-hidden="true"
      >
        {status === 'favorites'
          ? '★'
          : '0'}
      </span>

      <h2>{title}</h2>

      <p>{description}</p>

      <button
        type="button"
        onClick={onClear}
      >
        Clear filters
      </button>
    </div>
  );
}

function normalizeSearchValue(
  value
) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function normalizeTeamKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(
      /[^a-z0-9]/g,
      ''
    );
}

function normalizeConferenceKey(
  value
) {
  return normalizeConference(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(
      /[^a-z0-9]/g,
      ''
    );
}

function getTeamFavoriteKey(team) {
  const slug = canonicalTeamSlug(
    team?.slug || team?.name
  );

  if (!slug) {
    return '';
  }

  return `team:${slug}`;
}

function getTeamFavoriteKeys(team) {
  const primary = getTeamFavoriteKey(team);
  const legacyName = normalizeTeamKey(team?.name);
  return [primary, legacyName ? `name:${legacyName}` : ''].filter(Boolean);
}

function readFavoriteTeams() {
  if (
    typeof window ===
    'undefined'
  ) {
    return [];
  }

  try {
    const stored =
      window.localStorage.getItem(
        FAVORITES_STORAGE_KEY
      );

    if (!stored) {
      return [];
    }

    const parsed =
      JSON.parse(stored);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return Array.from(
      new Set(
        parsed
          .filter(
            (value) =>
              typeof value ===
              'string'
          )
          .map((value) =>
            value.trim()
          )
          .filter(Boolean)
      )
    );
  } catch {
    return [];
  }
}

function saveFavoriteTeams(
  favorites
) {
  if (
    typeof window ===
    'undefined'
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify(
        favorites
      )
    );
    window.queueMicrotask(() => {
      window.dispatchEvent(
        new CustomEvent('volleywire:favorites-changed', {
          detail: { favorites },
        })
      );
    });
  } catch {
    // Favorites still work for the
    // current page when storage is
    // unavailable.
  }
}

function getStatusGroup(game) {
  if (['suspended', 'postponed', 'canceled', 'rescheduled', 'unconfirmed'].includes(game?.status)) return 'other';
  if (
    game?.status === 'live'
  ) {
    return 'live';
  }

  if (
    game?.status === 'final'
  ) {
    return 'final';
  }

  return 'scheduled';
}

function matchesSearch(
  game,
  query
) {
  if (!query) {
    return true;
  }

  const searchableValues = [
    game?.away?.name,
    game?.away?.abbreviation,
    game?.away?.conference,
    game?.home?.name,
    game?.home?.abbreviation,
    game?.home?.conference,
    game?.venue,
    game?.network,
    game?.statusLabel,
  ];

  return searchableValues.some(
    (value) =>
      normalizeSearchValue(
        value
      ).includes(query)
  );
}

function matchesStatus({
  game,
  status,
  favorite,
}) {
  if (status === 'all') {
    return true;
  }

  if (
    status === 'favorites'
  ) {
    return favorite;
  }

  return (
    getStatusGroup(game) ===
    status
  );
}

function matchesConference({
  game,
  conference,
}) {
  if (
    conference === 'all'
  ) {
    return true;
  }

  return [
    game?.away?.conference,
    game?.home?.conference,
  ].some(
    (value) =>
      normalizeConferenceKey(
        value
      ) === conference
  );
}

function getConferenceOptions(
  games
) {
  const conferences =
    new Map();

  games.forEach((game) => {
    [
      game?.away?.conference,
      game?.home?.conference,
    ].forEach((value) => {
      const label = normalizeConference(value);

      const key =
        normalizeConferenceKey(
          label
        );

      if (
        !label ||
        !isDivisionOneConference(label) ||
        !key ||
        conferences.has(key)
      ) {
        return;
      }

      conferences.set(
        key,
        label
      );
    });
  });

  return Array.from(
    conferences,
    ([key, label]) => ({
      key,
      label,
    })
  ).sort((left, right) =>
    left.label.localeCompare(
      right.label,
      undefined,
      {
        sensitivity: 'base',
      }
    )
  );
}

function StatusSummaryButton({
  value,
  label,
  count,
  active,
  onSelect,
}) {
  const unavailable =
    count === 0;

  const matchLabel =
    count === 1
      ? 'match'
      : 'matches';

  return (
    <button
      type="button"
      className={`scoreboard-status-button ${value}${
        active ? ' active' : ''
      }${
        unavailable
          ? ' empty'
          : ''
      }`}
      aria-pressed={active}
      aria-label={
        unavailable
          ? `No ${label.toLowerCase()} matches on selected date`
          : `${label} on selected date: ${count} ${matchLabel}`
      }
      disabled={unavailable}
      onClick={() => {
        if (!unavailable) {
          onSelect(
            active
              ? 'all'
              : value
          );
        }
      }}
    >
      <span
        className="scoreboard-status-dot"
        aria-hidden="true"
      />

      <strong>{count}</strong>

      <span>{label}</span>
    </button>
  );
}

function PersonalFilters({ activeFilters, counts, onToggle }) {
  const items = [
    ['teams', 'Teams', '★', 'My Teams'],
    ['ranked', 'Top 25', '#', 'Top 25'],
    ['conferences', 'My Conf.', '', 'My Conferences'],
  ];
  return (
    <div className="scoreboard-personal-filters" aria-label="Personal match filters">
      {items.map(([key, label, icon, accessibleLabel]) => {
        const active = activeFilters.includes(key);
        return (
          <button
            type="button"
            key={key}
            className={`scoreboard-filter-button${active ? ' active' : ''}`}
            aria-pressed={active}
            aria-label={`Filter by ${accessibleLabel}`}
            onClick={() => onToggle(key)}
          >
            {icon ? <span aria-hidden="true">{icon}</span> : null}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ConferenceFilter({
  conferenceOptions,
  selectedConference,
  activePersonalFilters,
  personalFilterCounts,
  onChangeConference,
  onTogglePersonalFilter,
  onOpenSearch,
  searchActive,
}) {
  const sheetRef = useRef(null);
  const triggerRef = useRef(null);
  const selected = conferenceOptions.find((item) => item.key === selectedConference);
  const abbreviation = selected ? (selected.label.length <= 5 ? selected.label : selected.label.split(/\s+/).map((word) => word[0]).join('')) : '☷';
  function closeSheet() {
    sheetRef.current?.close();
    triggerRef.current?.focus();
  }
  return (
    <div className="scoreboard-conference-shell">
      <div
        className="scoreboard-conference-filter"
        aria-label="Filter matches by conference or favorite teams"
      >
        <PersonalFilters
          activeFilters={activePersonalFilters}
          counts={personalFilterCounts}
          onToggle={onTogglePersonalFilter}
        />

        <button
          type="button"
          className={`scoreboard-filter-button${
            selectedConference ===
            'all'
              ? ' active'
              : ''
          }`}
          aria-pressed={
            selectedConference ===
            'all'
          }
          onClick={() =>
            onChangeConference(
              'all'
            )
          }
        >
          All conferences
        </button>

        {conferenceOptions.map(
          (conference) => (
            <button
              type="button"
              key={conference.key}
              className={`scoreboard-filter-button${
                selectedConference ===
                conference.key
                  ? ' active'
                  : ''
              }`}
              aria-pressed={
                selectedConference ===
                conference.key
              }
              onClick={() =>
                onChangeConference(
                  conference.key
                )
              }
            >
              {conference.label}
            </button>
          )
        )}
      </div>

      <div className="scoreboard-conference-mobile">
        <PersonalFilters
          activeFilters={activePersonalFilters}
          counts={personalFilterCounts}
          onToggle={onTogglePersonalFilter}
        />

        <button ref={triggerRef} type="button" className="scoreboard-conference-trigger"
          aria-label={selected ? `Filter conferences: ${selected.label}` : 'Filter conferences'}
          aria-haspopup="dialog" onClick={() => sheetRef.current?.showModal()}>
          {abbreviation}
        </button>
        <dialog ref={sheetRef} className="scoreboard-conference-sheet" aria-labelledby="conference-sheet-title"
          onClick={(event) => { if (event.target === event.currentTarget) closeSheet(); }}>
          <header><h2 id="conference-sheet-title">Conference</h2><button type="button" onClick={closeSheet} aria-label="Close conference filter">×</button></header>
          <div className="conference-sheet-options">
            {[{ key: 'all', label: 'All Conferences' }, ...conferenceOptions].map((item) => (
              <button key={item.key} type="button" aria-pressed={item.key === selectedConference}
                onClick={() => { onChangeConference(item.key); closeSheet(); }}>
                <span>{item.label}</span><span aria-hidden="true">{item.key === selectedConference ? '✓' : ''}</span>
              </button>
            ))}
          </div>
        </dialog>

        <button
          type="button"
          className={`scoreboard-mobile-search-button${searchActive ? ' active' : ''}`}
          aria-label={searchActive ? 'Edit active scoreboard search' : 'Search matches'}
          aria-pressed={searchActive}
          onClick={onOpenSearch}
        >
          <span aria-hidden="true">⌕</span>
        </button>
      </div>

      <span
        className="scoreboard-conference-fade"
        aria-hidden="true"
      />
    </div>
  );
}

function ScoreboardControls({
  query,
  status,
  selectedConference,
  visibleCount,
  totalCount,
  activePersonalFilters,
  personalFilterCounts,
  statusCounts,
  conferenceOptions,
  onChangeQuery,
  onChangeStatus,
  onChangeConference,
  onTogglePersonalFilter,
  onClear,
}) {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchRef = useRef(null);
  const availableStatusGroups = STATUS_GROUPS.filter(
    (group) => statusCounts[group.value] > 0
  );
  const filtersActive =
    Boolean(query.trim()) ||
    status !== 'all' ||
    selectedConference !==
      'all';

  return (
    <div className={`scoreboard-controls scoreboard-controls-compact${mobileSearchOpen ? ' mobile-search-open' : ''}`}>
      {availableStatusGroups.length > 1 ? (
      <div
        className="scoreboard-status-summary"
        aria-label="Filter matches by status"
      >
        {availableStatusGroups.map(
          (group) => (
            <StatusSummaryButton
              key={group.value}
              value={group.value}
              label={
                group.countLabel
              }
              count={
                statusCounts[
                  group.value
                ]
              }
              active={
                status ===
                group.value
              }
              onSelect={
                onChangeStatus
              }
            />
          )
        )}
      </div>
      ) : null}

      <ConferenceFilter
        conferenceOptions={
          conferenceOptions
        }
        selectedConference={
          selectedConference
        }
        activePersonalFilters={activePersonalFilters}
        personalFilterCounts={personalFilterCounts}
        onChangeConference={
          onChangeConference
        }
        onTogglePersonalFilter={onTogglePersonalFilter}
        onOpenSearch={() => {
          setMobileSearchOpen(true);
          window.requestAnimationFrame(() => mobileSearchRef.current?.focus());
        }}
        searchActive={Boolean(query.trim())}
      />

      <div className="scoreboard-mobile-search">
        <label className="sr-only" htmlFor="scoreboard-mobile-search-input">
          Search matches
        </label>
        <span aria-hidden="true">⌕</span>
        <input
          ref={mobileSearchRef}
          id="scoreboard-mobile-search-input"
          type="search"
          value={query}
          onChange={(event) => onChangeQuery(event.target.value)}
          placeholder="Search teams or matches"
          autoComplete="off"
        />
        {query ? (
          <button
            type="button"
            className="scoreboard-mobile-search-clear"
            onClick={() => onChangeQuery('')}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
        <button
          type="button"
          className="scoreboard-mobile-search-cancel"
          onClick={() => {
            setMobileSearchOpen(false);
            window.requestAnimationFrame(() => {
              document.querySelector('.scoreboard-mobile-search-button')?.focus();
            });
          }}
        >
          Cancel
        </button>
      </div>

      <div className="scoreboard-search-row">
        <div className="scoreboard-search">
          <label
            className="sr-only"
            htmlFor="scoreboard-search-input"
          >
            Find a match
          </label>

          <div className="scoreboard-search-field">
            <span
              aria-hidden="true"
            >
              ⌕
            </span>

            <input
              id="scoreboard-search-input"
              type="search"
              value={query}
              onChange={(event) =>
                onChangeQuery(
                  event.target.value
                )
              }
              placeholder="Search teams, conferences, venues, or networks."
              autoComplete="off"
            />

            {query ? (
              <button
                type="button"
                onClick={() =>
                  onChangeQuery('')
                }
                aria-label="Clear scoreboard search"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>

        {filtersActive ? (
          <div className="scoreboard-result-summary">
            <span>
              <strong>
                {visibleCount}
              </strong>{' '}
              of {totalCount}{' '}
              {totalCount === 1
                ? 'match'
                : 'matches'}
            </span>

            <button
              type="button"
              onClick={onClear}
            >
              Clear filters
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ScoreboardGroup({
  label,
  value,
  games,
  onOpenMatch,
  isTeamFavorite,
  onToggleFavoriteTeam,
  hideHeading = false,
}) {
  if (!games.length) {
    return null;
  }

  return (
    <section
      className={`scoreboard-game-group ${value}`}
      aria-labelledby={hideHeading ? undefined : `scoreboard-group-${value}`}
      aria-label={hideHeading ? `${games.length} ${label.toLowerCase()} matches` : undefined}
    >
      {!hideHeading ? (
      <div className="scoreboard-group-heading">
        <h2
          id={`scoreboard-group-${value}`}
        >
          {label}
        </h2>

        <span
          aria-hidden="true"
        />

        {value !== 'live' ? <strong>{games.length} shown</strong> : null}
      </div>
      ) : null}

      <div
        className="score-grid"
        aria-label={`${games.length} ${label.toLowerCase()} ${
          games.length === 1
            ? 'match'
            : 'matches'
        }`}
      >
        {games.map(
          (
            game,
            index
          ) => (
            <ScoreCard
              key={
                game.id ||
                `${game.away?.name}-${game.home?.name}-${index}`
              }
              game={game}
              onOpenMatch={
                onOpenMatch
              }
              isTeamFavorite={
                isTeamFavorite
              }
              onToggleFavoriteTeam={
                onToggleFavoriteTeam
              }
              hideStatusLabel={value === 'scheduled'}
            />
          )
        )}
      </div>
    </section>
  );
}

export default function Scoreboard({
  loading,
  error,
  games = [],
  onRetry,
  onOpenMatch,
  onShowUpcoming,
  onHydrateGames,
}) {
  const [
    query,
    setQuery,
  ] = useState('');

  const [
    status,
    setStatus,
  ] = useState('all');

  const [
    selectedConference,
    setSelectedConference,
  ] = useState('all');

  const [
    favoriteTeams,
    setFavoriteTeams,
  ] = useState(
    readFavoriteTeams
  );

  const [favoriteConferences, setFavoriteConferences] = useState(readConferenceFavorites);
  const [activePersonalFilters, setActivePersonalFilters] = useState([]);

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_GAMES);

  const favoriteTeamKeys =
    useMemo(
      () =>
        new Set(
          favoriteTeams
        ),
      [favoriteTeams]
    );
  const favoriteConferenceKeys = useMemo(
    () => new Set(favoriteConferences),
    [favoriteConferences]
  );

  const conferenceOptions =
    useMemo(
      () =>
        getConferenceOptions(
          games
        ),
      [games]
    );

  const conferenceKeys =
    useMemo(
      () =>
        new Set(
          conferenceOptions.map(
            (conference) =>
              conference.key
          )
        ),
      [conferenceOptions]
    );

  const isTeamFavorite =
    useCallback(
      (team) => {
        return getTeamFavoriteKeys(team).some(
          (key) => favoriteTeamKeys.has(key)
        );
      },
      [favoriteTeamKeys]
    );

  const isGameFavorite =
    useCallback(
      (game) =>
        isTeamFavorite(
          game?.away
        ) ||
        isTeamFavorite(
          game?.home
        ),
      [isTeamFavorite]
    );

  const isGameRanked = useCallback(
    (game) => [game?.away, game?.home].some((team) => {
      const rank = Number(team?.rank);
      return Number.isInteger(rank) && rank >= 1 && rank <= 25;
    }),
    []
  );

  const isGameInFavoriteConference = useCallback(
    (game) => [game?.away, game?.home].some((team) => {
      const key = getTeamConferenceKey(team);
      return Boolean(key && favoriteConferenceKeys.has(key));
    }),
    [favoriteConferenceKeys]
  );

  useEffect(() => {
    function handleStorage(
      event
    ) {
      if (
        !event?.key ||
        event.key ===
        FAVORITES_STORAGE_KEY
      ) {
        setFavoriteTeams(
          readFavoriteTeams()
        );
      }

      if (!event?.key || event.key === CONFERENCE_FAVORITES_STORAGE_KEY) {
        setFavoriteConferences(readConferenceFavorites());
      }
    }

    window.addEventListener(
      'storage',
      handleStorage
    );
    window.addEventListener(
      'volleywire:favorites-changed',
      handleStorage
    );
    window.addEventListener(CONFERENCE_FAVORITES_EVENT, handleStorage);

    return () => {
      window.removeEventListener(
        'storage',
        handleStorage
      );
      window.removeEventListener(
        'volleywire:favorites-changed',
        handleStorage
      );
      window.removeEventListener(CONFERENCE_FAVORITES_EVENT, handleStorage);
    };
  }, []);

  useEffect(() => {
    if (
      selectedConference ===
        'all' ||
      conferenceKeys.has(
        selectedConference
      )
    ) {
      return;
    }

    setSelectedConference(
      'all'
    );
  }, [
    conferenceKeys,
    selectedConference,
  ]);

  const statusCounts =
    useMemo(() => {
      const counts = {
        live: 0,
        scheduled: 0,
        final: 0,
        other: 0,
      };

      games.forEach((game) => {
        counts[
          getStatusGroup(game)
        ] += 1;
      });

      return counts;
    }, [games]);

  useEffect(() => {
    if (
      [
        'live',
        'scheduled',
        'final',
        'other',
      ].includes(status) &&
      statusCounts[status] === 0
    ) {
      setStatus('all');
    }
  }, [
    status,
    statusCounts,
  ]);

  const favoriteGameCount =
    useMemo(
      () =>
        games.filter(
          isGameFavorite
        ).length,
      [
        games,
        isGameFavorite,
      ]
    );
  const personalFilterCounts = useMemo(() => ({
    teams: games.filter(isGameFavorite).length,
    ranked: games.filter(isGameRanked).length,
    conferences: games.filter(isGameInFavoriteConference).length,
  }), [games, isGameFavorite, isGameInFavoriteConference, isGameRanked]);

  const matchesPersonalFilters = useCallback((game) => {
    if (!activePersonalFilters.length) return true;
    return activePersonalFilters.some((filterName) =>
      filterName === 'teams'
        ? isGameFavorite(game)
        : filterName === 'ranked'
          ? isGameRanked(game)
          : filterName === 'conferences'
            ? isGameInFavoriteConference(game)
            : false
    );
  }, [activePersonalFilters, isGameFavorite, isGameInFavoriteConference, isGameRanked]);

  const filteredGames =
    useMemo(() => {
      const normalizedQuery =
        normalizeSearchValue(
          query
        );

      return games
        .filter((game) => {
          const favorite =
            isGameFavorite(
              game
            );

          return (
            matchesPersonalFilters(game) &&
            matchesStatus({
              game,
              status,
              favorite,
            }) &&
            matchesConference({
              game,
              conference:
                selectedConference,
            }) &&
            matchesSearch(
              game,
              normalizedQuery
            )
          );
        })
        .map(
          (
            game,
            originalIndex
          ) => ({
            game,
            originalIndex,
            ranked: isGameRanked(game),
            favorite:
              isGameFavorite(
                game
              ),
          })
        )
        .sort(
          (left, right) => {
            const statusPriority = {
              live: 0,
              other: 0.5,
              scheduled: 1,
              final: 2,
            };
            const leftStatus = getStatusGroup(left.game);
            const rightStatus = getStatusGroup(right.game);
            if (leftStatus !== rightStatus) {
              return statusPriority[leftStatus] - statusPriority[rightStatus];
            }

            if (
              left.favorite !==
              right.favorite
            ) {
              return left.favorite
                ? -1
                : 1;
            }

            // Within each status group: favorites, then Top 25, then others.
            if (left.ranked !== right.ranked) {
              return left.ranked ? -1 : 1;
            }

            const leftTime = Number(left.game?.startTimeEpoch);
            const rightTime = Number(right.game?.startTimeEpoch);
            if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
              const bothFinal = leftStatus === 'final';

              return bothFinal ? rightTime - leftTime : leftTime - rightTime;
            }

            return (
              left.originalIndex -
              right.originalIndex
            );
          }
        )
        .map(
          ({ game }) =>
            game
        );
    }, [
      games,
      isGameFavorite,
      isGameRanked,
      matchesPersonalFilters,
      query,
      selectedConference,
      status,
    ]);

  const groupedGames =
    useMemo(
      () =>
        STATUS_GROUPS.map(
          (group) => ({
            ...group,

            games:
              filteredGames.slice(0, visibleCount).filter(
                (game) =>
                  getStatusGroup(
                    game
                  ) ===
                  group.value
              ),
          })
        ),
      [filteredGames, visibleCount]
    );

  const singleStatusSlate =
    STATUS_GROUPS.filter(
      (group) => statusCounts[group.value] > 0
    ).length <= 1;

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_GAMES);
  }, [query, selectedConference, status, activePersonalFilters]);

  const hydrationGames = useMemo(
    // Records are needed for scheduled cards too, including newly filtered cards.
    () => filteredGames.slice(0, visibleCount),
    [filteredGames, visibleCount]
  );
  const hydrationGamesRef = useRef(hydrationGames);
  hydrationGamesRef.current = hydrationGames;

  const hydrationKey = hydrationGames
    .map((game) => [
      game?.gameId || game?.id || '',
      game?.status || '',
    ].join(':'))
    .join('|');

  useEffect(() => {
    if (!hydrationGames.length || typeof onHydrateGames !== 'function') return;
    const controller = new AbortController();
    let running = false;
    const hydrate = async () => {
      if (running || controller.signal.aborted || document.visibilityState === 'hidden') return;
      running = true;
      try { await onHydrateGames(hydrationGamesRef.current, { signal: controller.signal }); } catch { /* preserve cards */ }
      finally { running = false; }
    };
    hydrate();
    const timer = setInterval(hydrate, 20000);
    document.addEventListener('visibilitychange', hydrate);
    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', hydrate);
    };
  }, [hydrationKey, onHydrateGames]);

  const toggleFavoriteTeam =
    useCallback((team) => {
      const key =
        getTeamFavoriteKey(
          team
        );
      const keys = getTeamFavoriteKeys(team);

      if (!key) {
        return;
      }

      setFavoriteTeams(
        (current) => {
          const exists = keys.some((candidate) => current.includes(candidate));

          const next =
            exists
              ? current.filter(
                  (item) =>
                    !keys.includes(item)
                )
              : [
                  ...current,
                  key,
                ];

          saveFavoriteTeams(
            next
          );

          return next;
        }
      );
    }, []);

  function clearFilters() {
    setQuery('');
    setStatus('all');

    setSelectedConference(
      'all'
    );
    setActivePersonalFilters([]);
  }

  function togglePersonalFilter(filterName) {
    setActivePersonalFilters((current) =>
      current.includes(filterName)
        ? []
        : [filterName]
    );
  }

  if (loading) {
    return <LoadingScoreboard />;
  }

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={onRetry}
      />
    );
  }

  if (!games.length) {
    return <EmptyState onShowUpcoming={onShowUpcoming} />;
  }

  return (
    <>
      <ScoreboardControls
        query={query}
        status={status}
        selectedConference={
          selectedConference
        }
        visibleCount={
          filteredGames.length
        }
        totalCount={
          games.length
        }
        activePersonalFilters={activePersonalFilters}
        personalFilterCounts={personalFilterCounts}
        statusCounts={
          statusCounts
        }
        conferenceOptions={
          conferenceOptions
        }
        onChangeQuery={
          setQuery
        }
        onChangeStatus={
          setStatus
        }
        onChangeConference={
          setSelectedConference
        }
        onTogglePersonalFilter={togglePersonalFilter}
        onClear={
          clearFilters
        }
      />

      {!filteredGames.length ? (
        <FilteredEmptyState
          status={status}
          favoriteCount={
            favoriteTeams.length
          }
          activePersonalFilters={activePersonalFilters}
          personalFilterCounts={personalFilterCounts}
          selectedConference={
            selectedConference
          }
          query={query}
          onClear={
            clearFilters
          }
        />
      ) : (
        <>
          <div
            className="scoreboard-group-list"
            aria-live="polite"
          >
            {groupedGames.map(
              (group) => (
                <ScoreboardGroup
                  key={group.value}
                  label={group.label}
                  value={group.value}
                  games={group.games}
                  onOpenMatch={onOpenMatch}
                  isTeamFavorite={isTeamFavorite}
                  onToggleFavoriteTeam={toggleFavoriteTeam}
                  hideHeading={singleStatusSlate}
                />
              )
            )}
          </div>
          {visibleCount < filteredGames.length ? (
            <div className="scoreboard-load-more">
              <button
                type="button"
                onClick={() => setVisibleCount((current) => current + LOAD_MORE_GAMES)}
              >
                Load 30 more matches
              </button>
              <small>Showing {Math.min(visibleCount, filteredGames.length)} of {filteredGames.length}</small>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
